// when nesting a combination of DragSources / DropTargets / DragPreviews things can get weird
// and you may start to see a warning that looks like:
//
//     "Warning: setState(...): Cannot update during an existing state transition..."
//
// in that case it's probably due to re-rendering nested Drag/Drop objects. it's react-dnd calling
// setState in this case. here's the rules of thumb that seemed to work:
//
//   1. the DragPreview should never render a DragSource or DropTarget. this can happen when you're
//      reusing components for both. below, the `reorderablePreview` hoc injects a
//      beingUsedAsADragPreview prop for this purpose
//
//   2. the actual DragSource / DropTargets should not conditionally render nested Dragbables.
//      instead, favor always rendering them and hiding by wrapping in <div style="display: none;">
//
// for more info, peoples problems here proved immensely useful: https://www.bountysource.com/issues/32677234-warning-with-nested-dragsource

import React from 'react'
import { useDrag, useDrop, useDragLayer } from 'react-dnd'

const reorderable = (Component, type, options = {}) => {
  const { moveHandlerName, endDragHandlerName, validDropTarget } = options

  const Reorderable = React.forwardRef((props, ref) => {
    const nodeRef = React.useRef(null)
    const [{ isDragging }, dragRef, previewRef] = useDrag({
      type: type,
      // item: () => ({ ...props, startingOrder: props.order, itemType: type }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      item: (monitor) => {
        console.log('Begin drag, calling beginDragHandlerName');
        if (typeof props[options.beginDragHandlerName] === 'function') {
          props[options.beginDragHandlerName](type)
        }
        return { ...props, startingOrder: props.order, itemType: type }
      },
      end: (item, monitor) => {
        if (typeof props[options.clearDragTypeHandlerName] === 'function') {
          props[options.clearDragTypeHandlerName]()
        }
        if (type === item.itemType && item.startingOrder !== item.order) {
          if (typeof props[endDragHandlerName] === 'function') {
            props[endDragHandlerName]()
          }
        }
      }
    })

    React.useEffect(() => {
      previewRef(document.createElement('div'))
    }, [])

    const [{ canDrop }, dropRef] = useDrop({
      accept: type,
      canDrop: (item) => {
        let base = true
        if (validDropTarget) {
          base = base && validDropTarget(props, item)
        }
        return base && type === item.itemType
      },
      hover: (item, monitor) => {
        console.log('******ITEM HOVERING******')
        if (props.id === item.id) { return } // can't switch with yourself
        if (!monitor.canDrop()) { return } // if you can't drop here, you can't switch on hover either
        if (!nodeRef.current) {
          console.error('❌ nodeRef.current not found');
          return
        }
        // Get the DOM node for the current component
        const hoverBoundingRect = nodeRef.current?.getBoundingClientRect()
        if (!hoverBoundingRect) return

        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

        // Determine mouse position
        const clientOffset = monitor.getClientOffset()
        if (!clientOffset) return

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (item.order < props.order && hoverClientY < hoverMiddleY) {
          return
        }

        // Dragging upwards
        if (item.order > props.order && hoverClientY > hoverMiddleY) {
          return
        }

        props[moveHandlerName](item, props)

        // Update the monitor item order
        item.order = props.order
      },
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
      })
    })

    // Combine drag and drop refs
    React.useEffect(() => {
      dragRef(nodeRef.current)
      dropRef(nodeRef.current)
      if (ref) {
        ref.current = nodeRef.current
      }
    }, [dragRef, dropRef, ref])

    return (
      <div ref={nodeRef}>
        <div style={{ display: isDragging ? 'none' : 'block'  }}>
          <Component 
            {...props}
            connectDragSource={dragRef}
            connectDragPreview={previewRef}
            connectDropTarget={dropRef} 
            isDragging={isDragging}
            canDrop={canDrop}
          />
        </div>
      </div>
    )
  })

  return Reorderable
}

const reorderablePreview = (Component, type) => {
  const ReorderablePreview = (props) => {
    const {
      isDragging,
      item,
      initialSourceOffset,
      currentSourceOffset,
      differenceFromInitialOffset
    } = useDragLayer((monitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      itemType: monitor.getItemType(),
      initialSourceOffset: monitor.getInitialSourceClientOffset(),
      currentSourceOffset: monitor.getSourceClientOffset(),
      differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset()
    }))

    const previewRef = React.useRef(null)

    const getPreviewStyles = () => {
      if (!initialSourceOffset || !currentSourceOffset || !differenceFromInitialOffset) {
        return { display: 'none' }
      }

      // Pretty kludgy, just guess and checking that subtracting 40 makes things line up better
      let previewWidth = previewRef.current ? previewRef.current.clientWidth - 40 : 0

      let x = currentSourceOffset.x
      let y = currentSourceOffset.y

      let transform = `translate(${x}px, ${y}px)`

      return {
        position: 'absolute',
        opacity: 0.5,
        pointerEvents: 'none',
        transform: transform,
        WebkitTransform: transform,
        width: '60%',
        maxWidth: '600px'
      }
    }

    if (!isDragging || !item) return null

    // If we're not dragging something this component should be responsible for previewing
    if (item.itemType !== type) return null

    const layerStyles = {
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: 100,
      left: 0,
      top: 0,
      width: '100%',
      height: '100%'
    }

    return (
      <div style={layerStyles}>
        <div style={getPreviewStyles()} ref={previewRef}>
          <Component {...item} beingUsedAsADragPreview={true} />
        </div>
      </div>
    )
  }

  return ReorderablePreview
}

export { reorderable, reorderablePreview }
